import ArchiveIcon from '@assets/media/archive-icon.png';
import HomeIcon from '@assets/media/home-icon.png';
import LiveChannelsIcon from '@assets/media/live-channels-icon.png';
import MainSettingsIcon from '@assets/media/main-settings-icon.svg';
import MessagesIconUnread from '@assets/media/menu-message-icon-unread.svg';
import MessagesIcon from '@assets/media/menu-message-icon.svg';
import SearchIcon from '@assets/media/side-menu-search-icon.png';
import VodIcon from '@assets/media/vod-icon.png';
import {
  doesFocusableExist,
  FocusContext,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { Translation } from 'common/actions';
import { ExitPopup } from 'common/components/exit-popup';
import { Image } from 'common/components/image';
import { MainSettingsDrawer } from 'common/components/main-settings';
import ProductLogo from 'common/components/product-logo/ProductLogo';
import { P1 } from 'common/components/Typography';
import {
  APP_VERSION,
  ROUTES_WITH_HEADER,
  ROUTE_ARCHIVE,
  ROUTE_HOME,
  ROUTE_INBOX,
  ROUTE_SEARCH,
  ROUTE_TV_CHANNELS,
  ROUTE_VOD
} from 'common/config/constants';
import variables from 'common/config/variables';
import { useDrawer } from 'common/contexts/drawer';
import { usePopup } from 'common/contexts/popup';
import { FocusableItem } from 'common/interfaces';
import { getClassName, getKey, getRemoteKeyName } from 'common/utils';
import FocusHistory from 'common/utils/FocusHistory';
import { getPageName, getRootPath } from 'common/utils/helpers';
import {
  useMessages,
  useOpennedOverlay,
  usePageNavigation,
  useVisibilityState
} from 'common/utils/hooks';
import { isEmpty, throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import CloseMenuView from './CloseMenuView';
import DetailedInfo from './DetailedInfo';
import MenuItem from './MenuItem';
import { isMenuShown } from './utils';

const HOME_MENU_ITEM = getKey('menu-item', undefined, getRootPath(ROUTE_HOME));

interface EnabledMenuRoute {
  title: string;
  icon: string;
  path: string;
  disableBreadCrumb?: boolean;
  hidden?: boolean;
  onSelect?: () => void;
  active?: boolean;
  key?: string;
  testId?: string;
  disabled?: boolean;
}

type DisabledMenuRoute = {
  title: React.ReactNode;
  disabled: true;
  hidden?: boolean;
};

type MenuRoute = EnabledMenuRoute | DisabledMenuRoute;
interface SideMenuProps extends FocusableItem {
  focusKey?: string;
  initialOpen?: boolean;
  onMenuSelect?: () => void;
  onMenuVisibilityChange?: (visible: boolean) => void;
  // Redux injected
  i18n: Translation;
  isPlayerOpenned?: boolean;
  menuSubText?: string;
  hasVod?: boolean;
  //
}

function SideMenu({
  focusKey: focusKeyParam,
  i18n,
  isPlayerOpenned,
  onFocus,
  onBlur,
  onMenuSelect: onMenuSelectParam,
  onMenuVisibilityChange,
  initialOpen,
  menuSubText,
  hasVod
}: SideMenuProps) {
  const { pathname } = useLocation();
  const isAppActive = useVisibilityState();
  const { openDrawer, closeDrawer, getDrawers } = useDrawer();
  const { hasUnreadMessages, hasInboxMessages, unreadStandartMessages } = useMessages();
  const navigate = usePageNavigation();

  const settingsOpened = useMemo(
    () =>
      getDrawers()
        .map((x) => x.id)
        .includes('main-settings-drawer'),
    [getDrawers]
  );

  useEffect(() => {
    if (!isEmpty(unreadStandartMessages)) {
      // Show the message prompt
      setOpenedOverlay();
    } else {
      setClosedOverlay();
    }
  }, [unreadStandartMessages]);

  const returnToPlayer = useCallback(() => {
    // First force focus to the needed item in the menu so that the container
    // Remembers the last focused item
    const key = getKey('menu-item', undefined, ROUTE_HOME);
    setFocus(key);
    //
    setFocus('PLAYER');
    FocusHistory.clearHistoryForPath(location.pathname);
  }, []);

  const onArrowPress = useCallback((direction: string) => {
    if (direction === 'right') {
      returnToPlayer();
      return false;
    }
    return true;
  }, []);
  const menuIsShown = useMemo(() => isMenuShown(pathname), [pathname]);

  const { ref, focusKey, hasFocusedChild } = useFocusable({
    focusable: menuIsShown,
    focusKey: focusKeyParam,
    trackChildren: true,
    onFocus,
    onBlur
  });

  const {
    focused: menuIsActive,
    setOpenned: setOpenedOverlay,
    setClosed: setClosedOverlay
  } = useOpennedOverlay(hasFocusedChild);
  const { openPopup } = usePopup();

  const openMainSettingsDrawer = () => {
    setOpenedOverlay();
    openDrawer({
      id: 'main-settings-drawer',
      content: <MainSettingsDrawer />,
      positionContent: 'top',
      onClose: () => {
        closeDrawer('main-settings-drawer');
        setClosedOverlay();
      }
    });
  };

  const MENU_ROUTES: MenuRoute[][] = [
    [
      {
        title: i18n?.pageNames?.search,
        icon: SearchIcon,
        path: ROUTE_SEARCH,
        disableBreadCrumb: true,
        disabled: false
      }
    ],
    [
      {
        title: i18n?.pageNames?.home,
        icon: HomeIcon,
        path: ROUTE_HOME,
        disabled: false
      },
      {
        title: i18n?.pageNames?.channels,
        icon: LiveChannelsIcon,
        path: ROUTE_TV_CHANNELS,
        disabled: false
      },
      {
        title: i18n?.pageNames?.archive,
        icon: ArchiveIcon,
        path: ROUTE_ARCHIVE,
        disabled: false
      },
      {
        title: i18n?.pageNames?.vod,
        icon: VodIcon,
        path: ROUTE_VOD,
        hidden: !hasVod,
        disabled: false
      }
    ],
    [
      {
        title: i18n?.pageNames?.inbox,
        icon: hasUnreadMessages ? MessagesIconUnread : MessagesIcon,
        path: ROUTE_INBOX,
        disableBreadCrumb: false,
        hidden: !hasInboxMessages,
        disabled: false
      },
      {
        title: i18n?.mainSettings?.settings,
        icon: MainSettingsIcon,
        path: '',
        onSelect: openMainSettingsDrawer,
        active: settingsOpened,
        key: 'main-settings-menu-item',
        testId: 'side-menu-item-settings',
        disabled: false
      },
      {
        title: <P1 className="app-version">{APP_VERSION}</P1>,
        disabled: true
      }
    ]
  ];
  const MENU_ROUTES_FLAT = useRef<EnabledMenuRoute[]>([]);
  useEffect(() => {
    MENU_ROUTES_FLAT.current = MENU_ROUTES.flat().filter((e) => !e.disabled) as EnabledMenuRoute[];
  }, [MENU_ROUTES]);

  const menuIsVisible = useMemo(
    () => (menuIsShown && !isPlayerOpenned) || hasFocusedChild,
    [menuIsShown, isPlayerOpenned, hasFocusedChild]
  );
  const disableBreadcrumbForCurrentPage = useMemo(() => {
    const currentPage = MENU_ROUTES_FLAT.current.find((e) =>
      getRootPath(location.pathname).includes(e.path ?? '')
    );
    return currentPage?.disableBreadCrumb;
  }, [location.pathname, MENU_ROUTES_FLAT.current]);
  const [autoCloseTimerActive, setAutoCloseTimerActive] = useState(hasFocusedChild);

  // Always keep reference to the menu item for the current page
  // Initial value is home
  const menuItemForCurrentPageRef = useRef(HOME_MENU_ITEM);

  // Hack to get the new position of the selected element
  // Only on elements in the header menu
  // Used for initial page loading
  useEffect(() => {
    let focusKey = HOME_MENU_ITEM;
    if (
      menuIsShown &&
      MENU_ROUTES_FLAT.current.map((e) => e.path).includes(getRootPath(location.pathname)) &&
      menuItemForCurrentPageRef.current &&
      doesFocusableExist(menuItemForCurrentPageRef.current)
    ) {
      focusKey = menuItemForCurrentPageRef.current;
    }

    if (menuIsShown) {
      setFocus(focusKey);
    }
  }, [menuIsShown]);

  useEffect(() => {
    if (
      MENU_ROUTES_FLAT.current.find((e) => getRootPath(e.path) === getRootPath(location.pathname))
    ) {
      const key = getKey('menu-item', undefined, getRootPath(location.pathname));
      menuItemForCurrentPageRef.current = key;
    }
  }, [location.pathname]);

  // Reset auto close timer when activity is tracked
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const handleTimer = () => {
      setAutoCloseTimerActive(false);
      clearTimeout(timeout);
      timeout = setTimeout(() => setAutoCloseTimerActive(true), variables.THROTTLE_TIMEOUT);
    };
    if (menuIsShown && hasFocusedChild) {
      document.addEventListener('keydown', handleTimer);
      setAutoCloseTimerActive(true);
    }

    if (isAppActive && menuIsShown && hasFocusedChild && !settingsOpened) {
      setAutoCloseTimerActive(true);
    } else {
      setAutoCloseTimerActive(false);
    }

    return () => {
      document.removeEventListener('keydown', handleTimer);
      setAutoCloseTimerActive(false);
      clearTimeout(timeout);
    };
  }, [hasFocusedChild, isAppActive, menuIsShown, settingsOpened]);

  // Handle autoclose menu timeout
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (autoCloseTimerActive) {
      timeout = setTimeout(
        returnToPlayer,
        initialOpen
          ? variables.AUTO_CLOSE_SIDEMENU_INITIAL_TIMEOUT
          : variables.AUTO_CLOSE_SIDEMENU_TIMEOUT
      );
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [autoCloseTimerActive, initialOpen]);

  // Handle back navigation
  useEffect(() => {
    const handleKeyPress = throttle((e: KeyboardEvent) => {
      if (getRemoteKeyName(e.keyCode) === 'BACK') {
        if (!(menuIsVisible && hasFocusedChild)) return;
        const currentFocusKey = getCurrentFocusKey();
        const homeMenuItem = 'home-menu-item';
        // Check if focused on home
        if (currentFocusKey === homeMenuItem) {
          openPopup({
            id: 'EXIT-POPUP',
            content: <ExitPopup />,
            returnToLastFocused: true,
            onOpen: setOpenedOverlay,
            onClose: setClosedOverlay
          });
          return;
        }
        // Must be last
        else {
          setFocus(homeMenuItem);
        }
      }
    }, variables.THROTTLE_TIMEOUT);
    let timeout: NodeJS.Timeout;
    if (menuIsVisible && hasFocusedChild) {
      timeout = setTimeout(() => {
        document.addEventListener('keydown', handleKeyPress);
      }, variables.THROTTLE_TIMEOUT);
    }
    return () => {
      clearTimeout(timeout);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [menuIsVisible, hasFocusedChild, openPopup]);

  const onMenuSelect = useCallback(() => {
    onMenuSelectParam && onMenuSelectParam();
  }, [onMenuSelectParam]);

  const onNavigate = useCallback(() => {
    // Clear history for current path when user navigates to the menu
    FocusHistory.clearHistoryForPath(location.pathname);
  }, []);

  useEffect(() => {
    onMenuVisibilityChange && onMenuVisibilityChange(menuIsShown);
  }, [menuIsShown, onMenuVisibilityChange]);

  const renderMenu = () => {
    const items = MENU_ROUTES.map((outerRoutes, outerIndex) => {
      return (
        <div key={`inner-route-${outerIndex}`} className="menu-items">
          {outerRoutes
            .filter((e) => !e.hidden)
            .map((route) => {
              if (route.disabled) {
                return route.title;
              }
              const key = route.key || getKey('menu-item', undefined, route.path);
              const pageName = getPageName(route.path);
              const pageTitle = route.title || (isEmpty(i18n) ? '' : i18n.pageNames[pageName]);
              const active = settingsOpened ? false : getRootPath(location.pathname) === route.path;
              const onMenuSelectItem = () => {
                if (route.onSelect) {
                  route.onSelect();
                } else {
                  onMenuSelect();
                }
              };

              return (
                <MenuItem
                  key={key}
                  route={route}
                  disabled={!menuIsShown}
                  navigateOnFocus={false}
                  focusKey={key}
                  active={active && !hasFocusedChild && !menuIsActive}
                  onEnterPress={onMenuSelectItem}
                  onClick={onMenuSelectItem}
                  onArrowPress={onArrowPress}
                  testId={route.testId || `side-menu-item-${pageName}`}
                  onNavigate={onNavigate}
                >
                  <Image src={route.icon} className="menu-icon" />
                  <span className="menu-item-title">{pageTitle}</span>
                  <div
                    className={getClassName('menu-item-active-selector', {
                      active: route.active || active
                    })}
                    data-test-id={route.active || active ? 'side-menu-item-selector' : undefined}
                  />
                </MenuItem>
              );
            })}
        </div>
      );
    });

    return <>{items}</>;
  };

  const currentPageTitle = useMemo(() => {
    const routeConfig = MENU_ROUTES_FLAT.current
      .filter((route) => ROUTES_WITH_HEADER.includes(route.path || ''))
      .find((route) => route.path === getRootPath(pathname));
    if (!routeConfig) {
      return null;
    }
    return isEmpty(i18n) ? '' : i18n.pageNames[getPageName(routeConfig.path) || ''];
  }, [i18n, pathname]);

  const renderBreadcrumb = useCallback(() => {
    let pageTitle = currentPageTitle || '';
    if (Array.isArray(menuSubText) && menuSubText.length >= 2) {
      pageTitle = '...';
    }
    // render custom breadcrumb for current page or render the default breadcrumb
    return (
      <div
        className={getClassName('breadcrumb-container', {
          visible: !menuIsActive && menuIsVisible
        })}
      >
        {!disableBreadcrumbForCurrentPage && (
          <DetailedInfo testId="side-menu-breadcrumb" text={pageTitle} subText={menuSubText} />
        )}
      </div>
    );
  }, [menuIsActive, menuIsVisible, currentPageTitle, menuSubText, disableBreadcrumbForCurrentPage]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={getClassName('side-menu-wrapper', {
          visible: menuIsVisible,
          focused: menuIsActive,
          removed: !menuIsShown,
          'initial-open': initialOpen
        })}
        data-test-id="side-menu"
      >
        <div className="side-menu-mask" />
        <div className="product-logo-wrapper">
          <ProductLogo />
          {renderBreadcrumb()}
        </div>
        {renderMenu()}
      </div>
      <CloseMenuView
        active={autoCloseTimerActive}
        visible={menuIsShown && hasFocusedChild}
        initialOpen={initialOpen}
      />
    </FocusContext.Provider>
  );
}

const mapStateToProps = ({ i18n, videoPlayer, menu, epgs }: any) => {
  return {
    i18n,
    isPlayerOpenned: videoPlayer.video?.isFullscreen,
    menuSubText: menu.subText,
    hasVod:
      !isEmpty(epgs.vod.categories) && !isEmpty(epgs.vod.categoriesEpgs) && variables.VOD_ENABLED
  };
};

export default React.memo(connect(mapStateToProps, {})(SideMenu));
